'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/components/CartContext';
import { orderService, type OrderResult } from '@/lib/services/orderService';

type Step = 'contact' | 'shipping' | 'payment' | 'confirmation';

const steps: { key: Step; label: string; number: string }[] = [
  { key: 'contact', label: 'Contact', number: '01' },
  { key: 'shipping', label: 'Shipping', number: '02' },
  { key: 'payment', label: 'Payment', number: '03' },
];

const shippingMethods = [
  {
    id: 'standard',
    label: 'Standard Shipping',
    detail: '5–7 business days',
    price: 8.95,
    free_threshold: 75,
  },
  {
    id: 'express',
    label: 'Express Shipping',
    detail: '2–3 business days',
    price: 18.95,
    free_threshold: null,
  },
  {
    id: 'overnight',
    label: 'Overnight',
    detail: 'Next business day',
    price: 34.95,
    free_threshold: null,
  },
];

interface ContactForm {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface AddressForm {
  address: string;
  apartment: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface PaymentForm {
  cardNumber: string;
  cardName: string;
  expiry: string;
  cvv: string;
  sameAsBilling: boolean;
}

export default function CheckoutClient() {
  const { items, subtotal, clearCart } = useCart();
  const [step, setStep] = useState<Step>('contact');

  const [contact, setContact] = useState<ContactForm>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  });

  const [address, setAddress] = useState<AddressForm>({
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
  });

  const [selectedShipping, setSelectedShipping] = useState('standard');
  const [payment, setPayment] = useState<PaymentForm>({
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    sameAsBilling: true,
  });

  const [subscribeMarketing, setSubscribeMarketing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null);

  const shippingCost = (() => {
    const method = shippingMethods.find((m) => m.id === selectedShipping);
    if (!method) return 0;
    if (method.free_threshold && subtotal >= method.free_threshold) return 0;
    return method.price;
  })();

  const total = subtotal + shippingCost;

  const handleContactNext = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('shipping');
  };

  const handleShippingNext = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    const result = await orderService.createOrder({
      customer: {
        email: contact.email,
        firstName: contact.firstName,
        lastName: contact.lastName,
        phone: contact.phone,
        address: address.address,
        apartment: address.apartment,
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country,
        subscribeMarketing,
      },
      items: items.map((item) => ({
        productName: item.name,
        productImage: item.image,
        variant: item.variant,
        quantity: item.quantity,
        unitPrice: item.price,
        totalPrice: item.price * item.quantity,
      })),
      shippingMethod: selectedShipping as 'standard' | 'express' | 'overnight',
      shippingCost,
      subtotal,
      total,
    });

    setIsSubmitting(false);

    if (result) {
      setOrderResult(result);
      setStep('confirmation');
      clearCart();
    } else {
      setSubmitError('There was an issue placing your order. Please try again.');
    }
  };

  const formatCardNumber = (val: string) => {
    return val
      .replace(/\D/g, '')
      .replace(/(\d{4})/g, '$1 ')
      .trim()
      .slice(0, 19);
  };

  const formatExpiry = (val: string) => {
    return val
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .slice(0, 5);
  };

  const stepIndex = steps.findIndex((s) => s.key === step);

  if (step === 'confirmation') {
    const displayOrderNumber = orderResult?.orderNumber || '#LB-' + Math.floor(Math.random() * 90000 + 10000);
    return (
      <div
        style={{ paddingTop: '80px' }}
        className="min-h-screen flex items-center justify-center px-6"
      >
        <div className="max-w-lg w-full text-center py-16">
          {/* Success Icon */}
          <div
            className="w-20 h-20 mx-auto mb-8 flex items-center justify-center float-1"
            style={{
              background: 'rgba(201,169,110,0.1)',
              border: '2px solid rgba(201,169,110,0.3)',
            }}
          >
            <Icon name="CheckBadgeIcon" size={36} className="text-[#C9A96E]" />
          </div>

          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A96E]" />
            <span className="label-gold">Order Confirmed</span>
            <div className="w-8 h-px bg-[#C9A96E]" />
          </div>

          <h1
            className="font-fraunces text-[#1A1612] leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 400 }}
          >
            Thank you,
            <br />
            <em className="gradient-gold not-italic">{contact.firstName || 'Beautiful'}!</em>
          </h1>

          <p className="text-[15px] text-[#8A7E72] leading-relaxed mb-3">
            Your order has been confirmed and is being prepared with care. You&apos;ll receive a
            confirmation email at{' '}
            <strong className="text-[#1A1612]">{contact.email || 'your email'}</strong>.
          </p>

          <div
            className="my-8 p-6 text-left"
            style={{ background: 'white', border: '1px solid rgba(201,169,110,0.15)' }}
          >
            <div className="flex justify-between items-center mb-4">
              <p className="text-[11px] font-bold tracking-widest uppercase text-[#8A7E72]">
                Order Details
              </p>
              <span
                className="text-[9px] font-bold tracking-widest uppercase px-2 py-1"
                style={{ background: 'rgba(201,169,110,0.1)', color: '#C9A96E' }}
              >
                Processing
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7E72]">Order Number</span>
                <span className="font-semibold text-[#1A1612]">{displayOrderNumber}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7E72]">Estimated Delivery</span>
                <span className="font-semibold text-[#1A1612]">5–7 Business Days</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-[#8A7E72]">Total Charged</span>
                <span className="font-fraunces text-[17px] font-medium text-[#1A1612]">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {subscribeMarketing && (
            <div
              className="mb-8 p-4 flex items-start gap-3"
              style={{
                background: 'rgba(201,169,110,0.05)',
                border: '1px solid rgba(201,169,110,0.15)',
              }}
            >
              <Icon name="EnvelopeIcon" size={16} className="text-[#C9A96E] mt-0.5 flex-shrink-0" />
              <p className="text-[12px] text-[#8A7E72] leading-relaxed text-left">
                You&apos;re subscribed to LuxeBeauty updates. Expect exclusive offers, skincare
                tips, and early access to new collections.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Link href="/products">
              <button className="btn-gold w-full">Continue Shopping</button>
            </Link>
            <Link href="/homepage">
              <button className="btn-outline w-full">Back to Home</button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Header */}
      <div className="border-b border-[rgba(201,169,110,0.12)]">
        <div className="max-w-8xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-px bg-[#C9A96E]" />
            <span className="label-gold">Secure Checkout</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h1
              className="font-fraunces text-[#1A1612] leading-tight"
              style={{ fontSize: 'clamp(1.8rem, 3.5vw, 3rem)', fontWeight: 400 }}
            >
              Complete Your Order
            </h1>

            {/* Progress Steps */}
            <div className="flex items-center gap-3">
              {steps.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-6 h-6 flex items-center justify-center text-[9px] font-bold transition-all ${
                        i < stepIndex
                          ? 'bg-[#C9A96E] text-[#1A1612]'
                          : i === stepIndex
                            ? 'bg-[#1A1612] text-[#FAF8F3]'
                            : 'bg-transparent border border-[rgba(26,22,18,0.15)] text-[#B8AFA6]'
                      }`}
                    >
                      {i < stepIndex ? '✓' : s.number}
                    </div>
                    <span
                      className={`text-[11px] font-semibold tracking-wide hidden sm:block ${
                        i === stepIndex ? 'text-[#1A1612]' : 'text-[#B8AFA6]'
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className="w-8 h-px"
                      style={{
                        background: i < stepIndex ? '#C9A96E' : 'rgba(26,22,18,0.12)',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Form */}
          <div className="col-span-12 lg:col-span-7">
            {/* Step 1: Contact */}
            {step === 'contact' && (
              <form onSubmit={handleContactNext}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-px bg-[#C9A96E]" />
                  <h2 className="font-fraunces text-[22px] font-medium text-[#1A1612]">
                    Contact Information
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      placeholder="elena@example.com"
                      className="input-luxe"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                        First Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={contact.firstName}
                        onChange={(e) => setContact({ ...contact, firstName: e.target.value })}
                        placeholder="Elena"
                        className="input-luxe"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={contact.lastName}
                        onChange={(e) => setContact({ ...contact, lastName: e.target.value })}
                        placeholder="Marchetti"
                        className="input-luxe"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                      placeholder="+1 (310) 555-0124"
                      className="input-luxe"
                    />
                  </div>

                  {/* Shipping Address */}
                  <div className="pt-4">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-6 h-px bg-[#C9A96E]" />
                      <h3 className="font-fraunces text-[18px] font-medium text-[#1A1612]">
                        Shipping Address
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                          Street Address *
                        </label>
                        <input
                          type="text"
                          required
                          value={address.address}
                          onChange={(e) => setAddress({ ...address, address: e.target.value })}
                          placeholder="123 Rodeo Drive"
                          className="input-luxe"
                        />
                      </div>

                      <div>
                        <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                          Apartment, Suite, etc.
                        </label>
                        <input
                          type="text"
                          value={address.apartment}
                          onChange={(e) => setAddress({ ...address, apartment: e.target.value })}
                          placeholder="Apt 4B"
                          className="input-luxe"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                            City *
                          </label>
                          <input
                            type="text"
                            required
                            value={address.city}
                            onChange={(e) => setAddress({ ...address, city: e.target.value })}
                            placeholder="Los Angeles"
                            className="input-luxe"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                            State *
                          </label>
                          <input
                            type="text"
                            required
                            value={address.state}
                            onChange={(e) => setAddress({ ...address, state: e.target.value })}
                            placeholder="CA"
                            className="input-luxe"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                            ZIP Code *
                          </label>
                          <input
                            type="text"
                            required
                            value={address.zip}
                            onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                            placeholder="90210"
                            className="input-luxe"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                            Country *
                          </label>
                          <select
                            value={address.country}
                            onChange={(e) => setAddress({ ...address, country: e.target.value })}
                            className="select-luxe w-full"
                          >
                            <option>United States</option>
                            <option>Canada</option>
                            <option>United Kingdom</option>
                            <option>Australia</option>
                            <option>France</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Marketing Consent */}
                  <div
                    className="flex items-start gap-3 p-4 mt-2"
                    style={{
                      background: 'rgba(201,169,110,0.04)',
                      border: '1px solid rgba(201,169,110,0.12)',
                    }}
                  >
                    <input
                      type="checkbox"
                      id="marketing"
                      checked={subscribeMarketing}
                      onChange={(e) => setSubscribeMarketing(e.target.checked)}
                      className="checkbox-luxe mt-0.5"
                    />
                    <label
                      htmlFor="marketing"
                      className="text-[12px] text-[#8A7E72] leading-relaxed cursor-pointer"
                    >
                      <span className="font-semibold text-[#1A1612]">Stay in the loop.</span>{' '}
                      Receive exclusive offers, skincare tips from our dermatologists, and early
                      access to new collections. Unsubscribe anytime.
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <Link href="/cart">
                    <button type="button" className="btn-outline flex items-center gap-2">
                      <Icon name="ArrowLeftIcon" size={14} />
                      Back to Cart
                    </button>
                  </Link>
                  <button
                    type="submit"
                    className="btn-gold flex-1 flex items-center justify-center gap-2"
                  >
                    Continue to Shipping
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Shipping */}
            {step === 'shipping' && (
              <form onSubmit={handleShippingNext}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-px bg-[#C9A96E]" />
                  <h2 className="font-fraunces text-[22px] font-medium text-[#1A1612]">
                    Shipping Method
                  </h2>
                </div>

                {/* Address Summary */}
                <div
                  className="mb-6 p-4 flex items-start justify-between gap-4"
                  style={{
                    background: 'rgba(201,169,110,0.04)',
                    border: '1px solid rgba(201,169,110,0.12)',
                  }}
                >
                  <div>
                    <p className="text-[11px] font-bold tracking-widest uppercase text-[#8A7E72] mb-1">
                      Shipping to
                    </p>
                    <p className="text-[13px] font-medium text-[#1A1612]">
                      {contact.firstName} {contact.lastName}
                    </p>
                    <p className="text-[12px] text-[#8A7E72]">
                      {address.address}
                      {address.apartment ? `, ${address.apartment}` : ''}, {address.city},{' '}
                      {address.state} {address.zip}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep('contact')}
                    className="text-[11px] text-[#C9A96E] underline underline-offset-2 hover:text-[#A07840] transition-colors whitespace-nowrap"
                  >
                    Edit
                  </button>
                </div>

                {/* Shipping Options */}
                <div className="space-y-3 mb-8">
                  {shippingMethods.map((method) => {
                    const isFree = method.free_threshold && subtotal >= method.free_threshold;
                    const price = isFree ? 0 : method.price;

                    return (
                      <label
                        key={method.id}
                        className={`flex items-center gap-4 p-5 cursor-pointer transition-all ${
                          selectedShipping === method.id
                            ? 'border-2 border-[#C9A96E]'
                            : 'border border-[rgba(26,22,18,0.1)] hover:border-[rgba(201,169,110,0.3)]'
                        }`}
                        style={{
                          background:
                            selectedShipping === method.id ? 'rgba(201,169,110,0.04)' : 'white',
                        }}
                      >
                        <input
                          type="radio"
                          name="shipping"
                          value={method.id}
                          checked={selectedShipping === method.id}
                          onChange={() => setSelectedShipping(method.id)}
                          className="radio-luxe"
                        />
                        <div className="flex-1">
                          <p className="font-semibold text-[14px] text-[#1A1612]">{method.label}</p>
                          <p className="text-[12px] text-[#8A7E72]">{method.detail}</p>
                          {isFree && (
                            <p className="text-[10px] font-bold tracking-wide text-[#C9A96E] mt-0.5">
                              Free on orders over ${method.free_threshold}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {isFree ? (
                            <div>
                              <span className="text-[13px] line-through text-[#B8AFA6]">
                                ${method.price.toFixed(2)}
                              </span>
                              <span className="ml-2 text-[13px] font-bold text-[#C9A96E]">
                                Free
                              </span>
                            </div>
                          ) : (
                            <span className="font-fraunces text-[16px] font-medium text-[#1A1612]">
                              ${price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep('contact')}
                    className="btn-outline flex items-center gap-2"
                  >
                    <Icon name="ArrowLeftIcon" size={14} />
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn-gold flex-1 flex items-center justify-center gap-2"
                  >
                    Continue to Payment
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                </div>
              </form>
            )}

            {/* Step 3: Payment */}
            {step === 'payment' && (
              <form onSubmit={handlePaymentSubmit}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-px bg-[#C9A96E]" />
                  <h2 className="font-fraunces text-[22px] font-medium text-[#1A1612]">
                    Payment Details
                  </h2>
                </div>

                {/* Security Badge */}
                <div
                  className="mb-6 flex items-center gap-3 p-4"
                  style={{
                    background: 'rgba(201,169,110,0.04)',
                    border: '1px solid rgba(201,169,110,0.12)',
                  }}
                >
                  <Icon name="LockClosedIcon" size={16} className="text-[#C9A96E]" />
                  <p className="text-[12px] text-[#8A7E72]">
                    Your payment information is encrypted and secured with 256-bit SSL.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                      Card Number *
                    </label>
                    <input
                      type="text"
                      required
                      value={payment.cardNumber}
                      onChange={(e) =>
                        setPayment({ ...payment, cardNumber: formatCardNumber(e.target.value) })
                      }
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      className="input-luxe"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                      Name on Card *
                    </label>
                    <input
                      type="text"
                      required
                      value={payment.cardName}
                      onChange={(e) => setPayment({ ...payment, cardName: e.target.value })}
                      placeholder="Elena Marchetti"
                      className="input-luxe"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                        Expiry Date *
                      </label>
                      <input
                        type="text"
                        required
                        value={payment.expiry}
                        onChange={(e) =>
                          setPayment({ ...payment, expiry: formatExpiry(e.target.value) })
                        }
                        placeholder="MM/YY"
                        maxLength={5}
                        className="input-luxe"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold tracking-wider uppercase text-[#8A7E72] mb-2 block">
                        CVV *
                      </label>
                      <input
                        type="text"
                        required
                        value={payment.cvv}
                        onChange={(e) =>
                          setPayment({
                            ...payment,
                            cvv: e.target.value.replace(/\D/g, '').slice(0, 4),
                          })
                        }
                        placeholder="123"
                        maxLength={4}
                        className="input-luxe"
                      />
                    </div>
                  </div>

                  {/* Billing Address */}
                  <div className="pt-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={payment.sameAsBilling}
                        onChange={(e) =>
                          setPayment({ ...payment, sameAsBilling: e.target.checked })
                        }
                        className="checkbox-luxe"
                      />
                      <span className="text-[13px] text-[#8A7E72]">
                        Billing address same as shipping address
                      </span>
                    </label>
                  </div>

                  {/* Accepted Cards */}
                  <div className="flex items-center gap-3 pt-2">
                    <p className="text-[11px] text-[#B8AFA6]">We accept:</p>
                    {['Visa', 'MC', 'Amex', 'Discover'].map((card) => (
                      <span
                        key={card}
                        className="text-[9px] font-bold px-2 py-1 border border-[rgba(26,22,18,0.1)] text-[#8A7E72]"
                      >
                        {card}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep('shipping')}
                    className="btn-outline flex items-center gap-2"
                  >
                    <Icon name="ArrowLeftIcon" size={14} />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-gold flex-1 flex items-center justify-center gap-2 py-4 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="w-4 h-4 border-2 border-[#1A1612] border-t-transparent rounded-full animate-spin" />
                        Placing Order...
                      </>
                    ) : (
                      <>
                        <Icon name="LockClosedIcon" size={14} />
                        Place Order · ${total.toFixed(2)}
                      </>
                    )}
                  </button>
                </div>

                {submitError && (
                  <p className="text-[12px] text-red-500 text-center mt-3">{submitError}</p>
                )}

                <p className="text-[11px] text-[#B8AFA6] text-center mt-4 leading-relaxed">
                  By placing your order you agree to our{' '}
                  <a href="#" className="text-[#C9A96E] underline underline-offset-1">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-[#C9A96E] underline underline-offset-1">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </div>

          {/* Right: Order Summary */}
          <div className="col-span-12 lg:col-span-5">
            <div
              className="p-6 lg:p-8 sticky top-28"
              style={{ background: 'white', border: '1px solid rgba(201,169,110,0.15)' }}
            >
              <h3 className="font-fraunces text-[18px] font-medium text-[#1A1612] mb-6">
                Order Summary
              </h3>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={`${item.id}-${item.variant}`} className="flex gap-3 items-start">
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-14 h-18 overflow-hidden bg-[#F0EBE0]"
                        style={{ height: '70px' }}
                      >
                        <AppImage
                          src={item.image}
                          alt={`${item.name} order summary thumbnail`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold"
                        style={{ background: '#1A1612', color: '#FAF8F3' }}
                      >
                        {item.quantity}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[13px] text-[#1A1612] leading-tight">
                        {item.name}
                      </p>
                      {item.variant && <p className="text-[11px] text-[#8A7E72]">{item.variant}</p>}
                    </div>
                    <p className="font-fraunces text-[15px] font-medium text-[#1A1612] flex-shrink-0">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="divider-gold mb-4" />

              <div className="space-y-2.5 mb-4">
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#8A7E72]">Subtotal</span>
                  <span className="text-[#1A1612] font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#8A7E72]">Shipping</span>
                  <span
                    className={`font-medium ${shippingCost === 0 ? 'text-[#C9A96E]' : 'text-[#1A1612]'}`}
                  >
                    {shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
              </div>

              <div className="divider-gold mb-4" />

              <div className="flex justify-between mb-6">
                <span className="font-semibold text-[15px] text-[#1A1612]">Total</span>
                <span className="font-fraunces text-[22px] font-medium text-[#1A1612]">
                  ${total.toFixed(2)}
                </span>
              </div>

              {/* Trust */}
              <div className="space-y-2.5">
                {[
                  { icon: 'LockClosedIcon', text: '256-bit SSL encryption' },
                  { icon: 'ArrowPathIcon', text: '30-day hassle-free returns' },
                  { icon: 'TruckIcon', text: 'Carbon-neutral shipping' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2.5">
                    <Icon
                      name={item.icon as 'LockClosedIcon'}
                      size={13}
                      className="text-[#C9A96E]"
                    />
                    <span className="text-[12px] text-[#8A7E72]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
